import React, { Suspense } from "react";
import { lazyWithRetry } from "@/lib/lazyWithRetry";
import { LazyControlBoundary } from "@/main/react/LazyControlBoundary";
import { ServerAchievementsRow } from "@/main/react/user/achievementsServerRow";

// Type-only import() query: erased at runtime, so it pulls nothing into the eager graph while still
// giving the exact prop types.
type AchievementsRailProps = React.ComponentProps<
    (typeof import("@/main/react/user/AchievementsRail"))["AchievementsRail"]
>;

/**
 * The header trophy rail, code-split behind the chunk-resilience helper (AGENTS.md rule 26).
 *
 * Split for two reasons. It keeps embla, Radix Dialog and Radix Progress off the startup path
 * (rule 27's eager budget) — and it keeps them off the path of every profile view, not just the ones
 * where someone opens the cabinet. More importantly, a profile is the most crawled page on the site,
 * so the rail must never be something the page has to load before it can render.
 *
 * `lazyWithRetry` rather than a bare `import()`: a bare one has no recovery, and a browser holding a
 * stale `main_bundle.js` that references a chunk hash the origin no longer serves would leave this
 * silently unmounted for the life of that tab.
 */
const AchievementsRailImpl = lazyWithRetry(
    () =>
        import("@/main/react/user/AchievementsRail").then((m) => ({
            default: m.AchievementsRail,
        })),
    "achievements-rail",
);

/**
 * No skeleton, deliberately.
 *
 * Every other lazy island on this page renders a pulsing placeholder while its chunk loads. This one
 * must not: the server shipped a chip row that already lists the earned rungs in the same place
 * (rule 32), so a skeleton would replace correct, readable HTML with a grey rectangle.
 *
 * And not `null` either (corrected 2026-09-10). `null` was chosen on the belief that it "leaves the SSR
 * row visible", but that row is a child of the UserPage container and the UserPage mount has already
 * discarded it by the time this Suspense boundary exists. The fallback therefore puts the server's own
 * row back from the snapshot, unmarked, until the real component takes over (§9.2).
 */
export function AchievementsRailLazy(props: AchievementsRailProps) {
    // Containment (FI-070, 2026-09-15): a chunk that never arrives costs the rail, not the UserPage
    // island, and what the member already had is the server's row, so that goes back for good, MARKED
    // (`restored`): a terminal chunk failure is a terminal failure, and the row must not read as merely
    // pending to `islandHealthGuard.mjs`.
    return (
        <LazyControlBoundary
            controlName="achievements-rail"
            fallback={<ServerAchievementsRow username={props.username} restored={true} />}
        >
            <Suspense fallback={<ServerAchievementsRow username={props.username} restored={false} />}>
                <AchievementsRailImpl {...props} />
            </Suspense>
        </LazyControlBoundary>
    );
}

export default AchievementsRailLazy;
